"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { useDropzone } from "react-dropzone"
import { Turnstile } from "@marsidev/react-turnstile"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Upload, X, CheckCircle, Loader2, ChevronDown } from "lucide-react"
import { toast } from "sonner"
import { validateField, VALIDATION_RULES } from "@/lib/form-validation"
import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { communityAnalytics } from "@/lib/analytics"
import type { CommunityDiscoverySource } from "@/lib/community/discovery"
import { compressImage, formatFileSize, isCompressionSupported, stripExifData } from "@/lib/image-compression"
import { isHeicFile } from "@/lib/heic-to-jpeg"
import { UPLOAD_ACCEPT, UPLOAD_MAX_BYTES_COMMUNITY } from "@/lib/upload-constants"
import { ZipCodeInput, type ZipValidationResult } from "@/components/ui/zip-code-input"
import { Checkbox } from "@/components/ui/checkbox"
import {
  COMMUNITY_PROJECT_TIMEFRAMES,
  COMMUNITY_PROJECT_TIMEFRAME_LABELS,
  COMMUNITY_PROJECT_TYPES,
  COMMUNITY_PROJECT_TYPE_LABELS,
  COMMUNITY_STORY_LIMITS,
} from "@/lib/community/story"

interface FormData {
  title: string
  description: string
  zipCode: string
  grassType: string
  projectType: string
  photoTakenMonth: string
  projectTimeframe: string
  startingPoint: string
  stepsTaken: string
  outcome: string
  email: string
  socialHandle: string
}

type SubmittedPhoto = Pick<FormData, 'title' | 'grassType'>

const GRASS_TYPES = ['Unknown', ...Object.keys(GRASS_CHARACTERISTICS)]
const MAX_PHOTO_MONTH = new Date().toISOString().slice(0, 7)

// Cloudflare's test key is intentionally limited to local development. A
// production build without the real public key shows an unavailable state.
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ||
  (process.env.NODE_ENV === 'production' ? '' : '1x00000000000000000000AA')

// Upload status stages for progress feedback
type UploadStage = 'idle' | 'compressing' | 'uploading' | 'submitting' | 'done'

interface CompressionStats {
  originalSize: number
  compressedSize: number
  wasCompressed: boolean
  savedPercent?: number
}

interface UploadedAsset {
  url: string
  thumbnailUrl: string | null
}

interface PhotoSubmitFormProps {
  initialGrassType?: string | null
  initialSource?: CommunityDiscoverySource
}

export function PhotoSubmitForm({
  initialGrassType,
  initialSource,
}: PhotoSubmitFormProps = {}) {
  const safeInitialGrassType = initialGrassType &&
    Object.prototype.hasOwnProperty.call(GRASS_CHARACTERISTICS, initialGrassType)
    ? initialGrassType
    : ''
  const previewUrlRef = useRef<string | null>(null)
  const submissionIdRef = useRef<string | null>(null)
  const submissionSourceRef = useRef<CommunityDiscoverySource | undefined>(initialSource)
  const successHeadingRef = useRef<HTMLHeadingElement>(null)

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [submittedPhoto, setSubmittedPhoto] = useState<SubmittedPhoto | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)
  const [uploadStage, setUploadStage] = useState<UploadStage>('idle')
  const [compressionStats, setCompressionStats] = useState<CompressionStats | null>(null)
  const [isCompressing, setIsCompressing] = useState(false)
  const [uploadedAsset, setUploadedAsset] = useState<UploadedAsset | null>(null)
  const [publishingRightsConfirmed, setPublishingRightsConfirmed] = useState(false)
  const [publishingRightsError, setPublishingRightsError] = useState(false)
  const [showStory, setShowStory] = useState(false)
  // Open "More details" up front when the result screen prefilled a grass type,
  // otherwise the prefill is invisible behind a collapsed disclosure.
  const [showExtras, setShowExtras] = useState(() => Boolean(safeInitialGrassType))

  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    zipCode: '',
    grassType: safeInitialGrassType,
    projectType: '',
    photoTakenMonth: '',
    projectTimeframe: '',
    startingPoint: '',
    stepsTaken: '',
    outcome: '',
    email: '',
    socialHandle: ''
  })

  const [errors, setErrors] = useState<Partial<FormData>>({})
  const [zipValidation, setZipValidation] = useState<ZipValidationResult>({
    status: 'empty',
    zip: '',
  })

  const zipIsResolved =
    zipValidation.status === 'resolved' &&
    zipValidation.zip === formData.zipCode

  const replacePreviewUrl = (nextUrl: string | null) => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
    }

    previewUrlRef.current = nextUrl
    setPreviewUrl(nextUrl)
  }

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (isSuccess) successHeadingRef.current?.focus({ preventScroll: true })
  }, [isSuccess])

  useEffect(() => {
    // A Community entry point may suggest the identified grass type, but it
    // never supplies a photo or skips the fresh public-sharing confirmation.
    submissionSourceRef.current = initialSource
    if (safeInitialGrassType) {
      setFormData((current) => current.grassType
        ? current
        : { ...current, grassType: safeInitialGrassType })
    }
  }, [initialSource, safeInitialGrassType])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: (accepted, rejected) => {
      const rejection = rejected[0]
      if (rejection) {
        const code = rejection.errors[0]?.code
        if (code === 'file-too-large') {
          const sizeMB = (rejection.file.size / (1024 * 1024)).toFixed(1)
          const maxMB = Math.round(UPLOAD_MAX_BYTES_COMMUNITY / (1024 * 1024))
          toast.error(`File too large (${sizeMB}MB). Maximum size is ${maxMB}MB.`)
        } else if (code === 'file-invalid-type') {
          toast.error('Please select a JPG, PNG, WebP, or HEIC image.')
        } else if (code === 'too-many-files') {
          toast.error('Please select just one photo at a time.')
        } else {
          toast.error(rejection.errors[0]?.message || 'That file could not be used.')
        }
        return
      }
      if (accepted[0]) handleFileSelect(accepted[0])
    },
    accept: UPLOAD_ACCEPT,
    maxSize: UPLOAD_MAX_BYTES_COMMUNITY,
    multiple: false,
    disabled: isCompressing || isSubmitting,
    // The actual button below is the single keyboard and click trigger. The
    // surrounding surface remains available for drag-and-drop without adding
    // an unlabeled, duplicate focus stop.
    noClick: true,
    noKeyboard: true,
  })

  // Accepts a File directly so it can be wired to react-dropzone's onDrop or
  // any programmatic caller. Type and size pre-checks remain as a safety net
  // for non-dropzone entry points.
  const handleFileSelect = async (file: File) => {
    if (!file) return

    // A newly selected file must never reuse an upload from an earlier try.
    setUploadedAsset(null)
    submissionIdRef.current = null

    const isHeic = isHeicFile(file)

    if (!file.type.startsWith('image/') && !isHeic) {
      toast.error('Please select an image file (JPG, PNG, WebP, or HEIC)')
      return
    }

    if (file.size > UPLOAD_MAX_BYTES_COMMUNITY) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1)
      const maxSizeMB = Math.round(UPLOAD_MAX_BYTES_COMMUNITY / (1024 * 1024))
      toast.error(`File too large (${fileSizeMB}MB). Maximum size is ${maxSizeMB}MB.`)
      return
    }

    // This survives metadata/CAPTCHA retries for the selected upload. The
    // database uniqueness contract turns an ambiguous response retry into a
    // replay of the original submission instead of a second moderation row.
    submissionIdRef.current = crypto.randomUUID()

    const originalSize = file.size

    // HEIC/HEIF must always use compressImage so it is converted to a JPEG the
    // upload endpoint accepts. Other formats only need the compression path
    // when they exceed the client-side optimization threshold.
    if (isHeic || (isCompressionSupported() && file.size > 2 * 1024 * 1024)) {
      setIsCompressing(true)
      try {
        const result = await compressImage(file, {
          maxSizeMB: 2,
          maxWidthOrHeight: 1920,
          quality: 0.85
        })

        setSelectedFile(result.file)
        replacePreviewUrl(URL.createObjectURL(result.file))

        const reducedSize = result.compressedSize < originalSize
        if (reducedSize) {
          const savedPercent = Math.round((1 - result.compressedSize / originalSize) * 100)
          setCompressionStats({
            originalSize,
            compressedSize: result.compressedSize,
            wasCompressed: true,
            savedPercent
          })
          toast.success(`Photo optimized! Reduced from ${formatFileSize(originalSize)} to ${formatFileSize(result.compressedSize)}`)
        } else {
          setCompressionStats({
            originalSize,
            compressedSize: result.compressedSize,
            wasCompressed: false
          })
          if (isHeic) {
            toast.success('HEIC photo converted to JPEG and ready to upload.')
          }
        }
      } catch (error) {
        console.error('Compression error:', error)
        // Community photos are public. Never fall back to the original file,
        // because it may still contain GPS, device, and timestamp metadata.
        setSelectedFile(null)
        replacePreviewUrl(null)
        setCompressionStats(null)
        toast.error(
          error instanceof Error
            ? error.message
            : isHeic
              ? 'We could not convert this HEIC photo. Try saving it as JPEG and uploading again.'
              : 'We could not safely prepare this photo. Try saving a new copy and uploading again.'
        )
        return
      } finally {
        setIsCompressing(false)
      }
    } else {
      // File is small enough for size, but still strip EXIF for privacy
      setIsCompressing(true)
      try {
        const cleanedFile = await stripExifData(file)
        if (cleanedFile === file) {
          throw new Error('Photo metadata could not be removed')
        }
        setSelectedFile(cleanedFile)
        replacePreviewUrl(URL.createObjectURL(cleanedFile))
        setCompressionStats({
          originalSize,
          compressedSize: cleanedFile.size,
          wasCompressed: false
        })
      } catch (error) {
        console.error('Metadata removal error:', error)
        // The public-gallery privacy promise is fail-closed: an unsanitized
        // original must never become the selected upload.
        setSelectedFile(null)
        replacePreviewUrl(null)
        setCompressionStats(null)
        toast.error('We could not remove this photo\'s private metadata. Try saving a new copy and uploading again.')
      } finally {
        setIsCompressing(false)
      }
    }
  }

  const removeFile = () => {
    setSelectedFile(null)
    replacePreviewUrl(null)
    setCompressionStats(null)
    setUploadedAsset(null)
    submissionIdRef.current = null
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters'
    }

    if (formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters'
    }

    // The whole lawn story is optional; only validate what was provided.
    if (formData.photoTakenMonth) {
      if (formData.photoTakenMonth < '2000-01') {
        newErrors.photoTakenMonth = 'Photo month must be January 2000 or later'
      } else if (formData.photoTakenMonth > MAX_PHOTO_MONTH) {
        newErrors.photoTakenMonth = 'Photo month cannot be in the future'
      }
    }

    const storyTextRules = [
      {
        key: 'startingPoint' as const,
        label: 'Starting point',
        limits: COMMUNITY_STORY_LIMITS.startingPoint,
      },
      {
        key: 'stepsTaken' as const,
        label: 'What you did',
        limits: COMMUNITY_STORY_LIMITS.stepsTaken,
      },
      {
        key: 'outcome' as const,
        label: 'Result',
        limits: COMMUNITY_STORY_LIMITS.outcome,
      },
    ]

    for (const { key, label, limits } of storyTextRules) {
      const length = formData[key].trim().length
      if (length === 0) continue
      if (length < limits.min) {
        newErrors[key] = `${label} needs at least ${limits.min} characters, or leave it blank`
      } else if (length > limits.max) {
        newErrors[key] = `${label} must be ${limits.max} characters or fewer`
      }
    }

    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required'
    } else {
      const zipResult = validateField({
        value: formData.zipCode,
        rules: VALIDATION_RULES.ZIP_CODE,
        fieldName: 'ZIP Code'
      })
      if (!zipResult.isValid) {
        newErrors.zipCode = zipResult.errors[0]
      } else if (!zipIsResolved) {
        newErrors.zipCode =
          zipValidation.status === 'invalid' && zipValidation.zip === formData.zipCode
            ? "We couldn't find that U.S. ZIP code. Check it and try again."
            : 'Wait for the ZIP code check to finish.'
      }
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    setPublishingRightsError(!publishingRightsConfirmed)

    // Errors inside a collapsed disclosure are invisible; reopen the section
    // so the inline messages mount.
    const errorKeys = Object.keys(newErrors) as Array<keyof FormData>
    if (errorKeys.some((key) => ['projectType', 'photoTakenMonth', 'projectTimeframe', 'startingPoint', 'stepsTaken', 'outcome'].includes(key))) {
      setShowStory(true)
    }
    // Keys listed here must stay in sync with the fields each section renders,
    // including ones that have no validation rule today (grassType, projectType,
    // projectTimeframe) so adding one later cannot strand its error out of view.
    if (errorKeys.some((key) => ['description', 'grassType', 'email', 'socialHandle'].includes(key))) {
      setShowExtras(true)
    }

    setErrors(newErrors)
    return errorKeys.length === 0 && publishingRightsConfirmed
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!selectedFile) {
      toast.error('Please select a photo to upload')
      return
    }

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    // Require CAPTCHA verification in production
    if (!turnstileToken && process.env.NODE_ENV === 'production') {
      toast.error('Please complete the CAPTCHA verification')
      return
    }

    try {
      setIsSubmitting(true)
      let asset = uploadedAsset

      if (!asset) {
        setUploadStage('uploading')

        // Upload the image (already compressed client-side).
        const imageFormData = new FormData()
        imageFormData.append('file', selectedFile)
        imageFormData.append('category', 'lawn-photos')

        const uploadResponse = await fetch('/api/upload/image', {
          method: 'POST',
          body: imageFormData
        })

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json()
          throw new Error(errorData.error || 'Failed to upload image')
        }

        const uploadData = await uploadResponse.json()

        if (!uploadData.success || !uploadData.url) {
          throw new Error(uploadData.error || 'Upload failed')
        }

        asset = {
          url: uploadData.url,
          thumbnailUrl: uploadData.thumbnailUrl || null,
        }
        // Keep the completed upload across a metadata/CAPTCHA retry so a
        // transient failure does not create another storage object.
        setUploadedAsset(asset)
      }

      setUploadStage('submitting')

      if (!submissionIdRef.current) {
        // Defensive fallback for a programmatic caller that supplied a file
        // without going through handleFileSelect.
        submissionIdRef.current = crypto.randomUUID()
      }

      // Now submit the photo data
      const photoData = {
        submissionId: submissionIdRef.current,
        imageUrl: asset.url,
        thumbnailUrl: asset.thumbnailUrl,
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        zipCode: formData.zipCode.trim(),
        grassType: formData.grassType === 'Unknown' ? null : (formData.grassType || null),
        projectType: formData.projectType || null,
        photoTakenMonth: formData.photoTakenMonth || null,
        projectTimeframe: formData.projectTimeframe || null,
        startingPoint: formData.startingPoint.trim() || null,
        stepsTaken: formData.stepsTaken.trim() || null,
        outcome: formData.outcome.trim() || null,
        email: formData.email.trim() || null,
        socialHandle: formData.socialHandle.trim() || null,
        turnstileToken,
        publishingRightsConfirmed,
      }

      const submitResponse = await fetch('/api/community/photos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(photoData)
      })

      if (!submitResponse.ok) {
        const errorData = await submitResponse.json()
        throw new Error(errorData.error || 'Failed to submit photo')
      }

      // Track community photo submission
      communityAnalytics.photoSubmitted(
        formData.grassType || 'unknown',
        'free',
        Boolean(formData.email),
        submissionSourceRef.current ? { source: submissionSourceRef.current } : undefined,
      )

      // Show success screen
      setSubmittedPhoto({
        title: formData.title.trim(),
        grassType: formData.grassType,
      })
      setUploadStage('done')
      setIsSuccess(true)

      // Reset form
      setSelectedFile(null)
      replacePreviewUrl(null)
      setCompressionStats(null)
      setUploadedAsset(null)
      submissionIdRef.current = null
      setPublishingRightsConfirmed(false)
      setPublishingRightsError(false)
      setTurnstileToken(null)
      setFormData({
        title: '',
        description: '',
        zipCode: '',
        grassType: '',
        projectType: '',
        photoTakenMonth: '',
        projectTimeframe: '',
        startingPoint: '',
        stepsTaken: '',
        outcome: '',
        email: '',
        socialHandle: ''
      })
      setZipValidation({ status: 'empty', zip: '' })

    } catch (error) {
      console.error('Submit error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to submit photo')
      // Turnstile tokens are single-use. Remount the widget for an honest retry
      // while retaining any already-uploaded photo.
      setTurnstileToken(null)
      setTurnstileResetKey((value) => value + 1)
    } finally {
      setIsSubmitting(false)
      setUploadStage('idle')
    }
  }

  // Show success screen if submission was successful
  if (isSuccess) {
    return (
      <div className="ds-glass mx-auto w-full overflow-hidden rounded-[2rem]">
        <div className="px-4 py-8 text-center sm:px-6 md:py-12">
          <div className="flex flex-col items-center space-y-4">
            <div className="rounded-full bg-green-100 p-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>

            <div className="space-y-1" role="status">
              <h2
                ref={successHeadingRef}
                tabIndex={-1}
                className="text-xl md:text-2xl font-bold text-gray-900 focus:outline-none"
              >
                Photo Submitted!
              </h2>
              <p className="text-sm text-gray-600">
                {submittedPhoto?.title ? (
                  <>
                    Thanks for sharing{' '}
                    <span className="font-semibold text-gray-900">{submittedPhoto.title}</span>
                    {submittedPhoto.grassType ? <> ({submittedPhoto.grassType})</> : null}. A moderator will review it before it appears in the gallery.
                  </>
                ) : (
                  <>A moderator will review your photo before it appears in the gallery.</>
                )}
              </p>
            </div>

            <div className="flex flex-col items-center gap-3 pt-2 w-full">
              <Button asChild size="lg" className="min-h-[48px] w-full sm:w-auto">
                <Link href="/community">See the Gallery</Link>
              </Button>
              <button
                type="button"
                onClick={() => {
                  setIsSuccess(false)
                  setSubmittedPhoto(null)
                  setUploadStage('idle')
                }}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 underline underline-offset-4 min-h-[44px]"
              >
                <Upload className="w-4 h-4" />
                Submit another
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="ds-glass mx-auto w-full overflow-hidden rounded-[2rem]">
      <div className="p-4 pb-2 text-center sm:p-6 sm:pb-2">
        <h2 className="font-editorial text-xl font-semibold tracking-tight text-green-950 md:text-2xl">Share Your Lawn Photo</h2>
        <p className="mt-1 text-sm text-gray-500">
          A photo, a title, and your ZIP is all it takes
        </p>
      </div>
      <div className="px-4 pb-4 pt-4 sm:px-6 sm:pb-6">
        <form onSubmit={handleSubmit} noValidate className="space-y-4 md:space-y-6">
          {/* File Upload */}
          <div>
            <Label htmlFor="photo">Photo *</Label>
            {!selectedFile && !isCompressing ? (
              <div className="mt-2">
                <div
                  {...getRootProps({
                    className: `border-2 border-dashed rounded-lg p-6 md:p-8 text-center transition-colors ${
                      isDragActive
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-300'
                    }`,
                  })}
                >
                  <input {...getInputProps({ id: 'photo' })} />
                  <Upload className="w-10 h-10 md:w-12 md:h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-4 text-sm md:text-base">
                    {isDragActive
                      ? 'Drop your photo here'
                      : 'Choose a photo to upload, or drag one here'}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      className="min-h-[48px] text-base"
                      onClick={(e) => {
                        e.stopPropagation()
                        open()
                      }}
                    >
                      <Upload className="w-5 h-5 mr-2" />
                      Choose File
                    </Button>
                  </div>
                  <p className="text-xs md:text-sm text-gray-500 mt-3">
                    JPG, PNG, WebP, or HEIC up to 10MB
                    <br />
                    <span className="text-green-600">Large photos are automatically optimized</span>
                  </p>
                </div>
              </div>
            ) : isCompressing ? (
              <div className="mt-2">
                <div className="border-2 border-dashed border-green-300 rounded-lg p-8 text-center bg-green-50/50">
                  <Loader2 className="w-10 h-10 text-green-600 mx-auto mb-3 animate-spin" />
                  <p className="text-green-700 font-medium">Optimizing your photo...</p>
                  <p className="text-sm text-green-600 mt-1">This may take a moment for large images</p>
                </div>
              </div>
            ) : (
              <div className="mt-2 relative">
                <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100">
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 w-10 h-10 rounded-full"
                    onClick={removeFile}
                    aria-label="Remove selected photo"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                {/* File info with compression stats */}
                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-100">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-green-800 truncate">
                        {selectedFile?.name}
                      </p>
                      {compressionStats && (
                        <div className="text-xs text-green-600 mt-1">
                          {compressionStats.wasCompressed ? (
                            <span className="flex items-center gap-1.5 flex-wrap">
                              <span className="line-through text-gray-400">
                                {formatFileSize(compressionStats.originalSize)}
                              </span>
                              <span>→</span>
                              <span className="font-medium">
                                {formatFileSize(compressionStats.compressedSize)}
                              </span>
                              <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs font-medium">
                                {compressionStats.savedPercent}% smaller
                              </span>
                            </span>
                          ) : (
                            <span>{formatFileSize(compressionStats.compressedSize)} • Ready to upload</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              type="text"
              placeholder="e.g., My Kentucky Bluegrass After Overseeding"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              maxLength={100}
              aria-invalid={errors.title ? 'true' : 'false'}
              aria-describedby="community-photo-title-help"
            />
            {errors.title ? (
              <p id="community-photo-title-help" role="alert" className="text-xs text-red-600 mt-1">{errors.title}</p>
            ) : (
              <p id="community-photo-title-help" className="text-xs text-gray-400 mt-1">
                {formData.title.length}/100
              </p>
            )}
          </div>

          {/* ZIP Code */}
          <div>
            <ZipCodeInput
              id="zipCode"
              label="ZIP code *"
              value={formData.zipCode}
              onChange={(zip) => {
                setFormData(prev => ({ ...prev, zipCode: zip }))
                setErrors(prev => {
                  if (!prev.zipCode) return prev
                  const next = { ...prev }
                  delete next.zipCode
                  return next
                })
              }}
              onValidationChange={(result) => {
                setZipValidation(result)
                if (result.status === 'resolved') {
                  setErrors(prev => {
                    if (!prev.zipCode) return prev
                    const next = { ...prev }
                    delete next.zipCode
                    return next
                  })
                }
              }}
              error={errors.zipCode || undefined}
            />
            {!errors.zipCode && (
              <p className="text-xs text-gray-500 mt-1">
                We store and show only the first three digits for regional leaderboards.
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-green-950/10 bg-green-50/45">
            <button
              type="button"
              onClick={() => setShowStory((open) => !open)}
              aria-expanded={showStory}
              aria-controls="lawn-story-section"
              className="flex w-full items-center justify-between gap-3 p-4 text-left md:p-5"
            >
              <span>
                <span className="block text-base font-semibold text-green-950">
                  Add the story behind it
                  <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 align-middle">Optional</span>
                </span>
                <span className="mt-1 block text-sm leading-5 text-green-950/70">
                  Project details help another homeowner learn what actually worked.
                </span>
              </span>
              <ChevronDown
                aria-hidden="true"
                className={`h-5 w-5 shrink-0 text-green-950/60 transition-transform ${showStory ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Always mounted, toggled with `hidden`, so the button's
                aria-controls IDREF always resolves. */}
            <fieldset id="lawn-story-section" hidden={!showStory} className="px-4 pb-4 md:px-5 md:pb-5">
            <legend className="sr-only">Lawn story (optional)</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="projectType">Project type</Label>
                <Select
                  value={formData.projectType}
                  onValueChange={(value) => {
                    setFormData((current) => ({ ...current, projectType: value }))
                    setErrors((current) => ({ ...current, projectType: undefined }))
                  }}
                >
                  <SelectTrigger
                    id="projectType"
                    aria-invalid={errors.projectType ? 'true' : 'false'}
                    aria-describedby="community-project-type-help"
                    className="bg-white"
                  >
                    <SelectValue placeholder="Choose a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMUNITY_PROJECT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {COMMUNITY_PROJECT_TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.projectType && (
                  <p id="community-project-type-help" role="alert" className="mt-1 text-xs text-red-600">
                    {errors.projectType}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="photoTakenMonth">Photo month</Label>
                <Input
                  id="photoTakenMonth"
                  type="month"
                  value={formData.photoTakenMonth}
                  min="2000-01"
                  max={MAX_PHOTO_MONTH}
                  onChange={(event) => {
                    setFormData((current) => ({ ...current, photoTakenMonth: event.target.value }))
                    setErrors((current) => ({ ...current, photoTakenMonth: undefined }))
                  }}
                  aria-invalid={errors.photoTakenMonth ? 'true' : 'false'}
                  aria-describedby="community-photo-month-help"
                  className="bg-white"
                />
                <p
                  id="community-photo-month-help"
                  role={errors.photoTakenMonth ? 'alert' : undefined}
                  className={`mt-1 text-xs ${errors.photoTakenMonth ? 'text-red-600' : 'text-green-950/60'}`}
                >
                  {errors.photoTakenMonth || 'Only the month and year are collected.'}
                </p>
              </div>

              <div className="sm:col-span-2">
                <Label htmlFor="projectTimeframe">How long did the work take?</Label>
                <Select
                  value={formData.projectTimeframe}
                  onValueChange={(value) => {
                    setFormData((current) => ({ ...current, projectTimeframe: value }))
                    setErrors((current) => ({ ...current, projectTimeframe: undefined }))
                  }}
                >
                  <SelectTrigger
                    id="projectTimeframe"
                    aria-invalid={errors.projectTimeframe ? 'true' : 'false'}
                    aria-describedby="community-project-timeframe-help"
                    className="bg-white"
                  >
                    <SelectValue placeholder="Choose a timeframe" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMUNITY_PROJECT_TIMEFRAMES.map((timeframe) => (
                      <SelectItem key={timeframe} value={timeframe}>
                        {COMMUNITY_PROJECT_TIMEFRAME_LABELS[timeframe]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.projectTimeframe && (
                  <p id="community-project-timeframe-help" role="alert" className="mt-1 text-xs text-red-600">
                    {errors.projectTimeframe}
                  </p>
                )}
              </div>
            </div>

            <div className="relative mt-6 space-y-5 before:absolute before:bottom-4 before:left-[13px] before:top-4 before:w-px before:bg-green-200">
              <div className="relative pl-9">
                <span aria-hidden="true" className="absolute left-0 top-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-green-700 text-xs font-bold text-white">1</span>
                <Label htmlFor="startingPoint">Starting point</Label>
                <Textarea
                  id="startingPoint"
                  placeholder="What did the lawn look like, and what problem were you trying to solve?"
                  value={formData.startingPoint}
                  onChange={(event) => {
                    setFormData((current) => ({ ...current, startingPoint: event.target.value }))
                    setErrors((current) => ({ ...current, startingPoint: undefined }))
                  }}
                  rows={3}
                  maxLength={COMMUNITY_STORY_LIMITS.startingPoint.max}
                  aria-invalid={errors.startingPoint ? 'true' : 'false'}
                  aria-describedby="community-starting-point-help"
                  className="mt-1.5 bg-white"
                />
                <p
                  id="community-starting-point-help"
                  role={errors.startingPoint ? 'alert' : undefined}
                  className={`mt-1 text-xs ${errors.startingPoint ? 'text-red-600' : 'text-green-950/55'}`}
                >
                  {errors.startingPoint || `${formData.startingPoint.length}/${COMMUNITY_STORY_LIMITS.startingPoint.max} characters (at least ${COMMUNITY_STORY_LIMITS.startingPoint.min} if filled in)`}
                </p>
              </div>

              <div className="relative pl-9">
                <span aria-hidden="true" className="absolute left-0 top-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-green-700 text-xs font-bold text-white">2</span>
                <Label htmlFor="stepsTaken">What did you do?</Label>
                <Textarea
                  id="stepsTaken"
                  placeholder="Share the useful details: seed or sod, mowing, watering, fertilizer, treatments, or timing."
                  value={formData.stepsTaken}
                  onChange={(event) => {
                    setFormData((current) => ({ ...current, stepsTaken: event.target.value }))
                    setErrors((current) => ({ ...current, stepsTaken: undefined }))
                  }}
                  rows={4}
                  maxLength={COMMUNITY_STORY_LIMITS.stepsTaken.max}
                  aria-invalid={errors.stepsTaken ? 'true' : 'false'}
                  aria-describedby="community-steps-taken-help"
                  className="mt-1.5 bg-white"
                />
                <p
                  id="community-steps-taken-help"
                  role={errors.stepsTaken ? 'alert' : undefined}
                  className={`mt-1 text-xs ${errors.stepsTaken ? 'text-red-600' : 'text-green-950/55'}`}
                >
                  {errors.stepsTaken || `${formData.stepsTaken.length}/${COMMUNITY_STORY_LIMITS.stepsTaken.max} characters (at least ${COMMUNITY_STORY_LIMITS.stepsTaken.min} if filled in)`}
                </p>
              </div>

              <div className="relative pl-9">
                <span aria-hidden="true" className="absolute left-0 top-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-green-700 text-xs font-bold text-white">3</span>
                <Label htmlFor="outcome">How did it turn out?</Label>
                <Textarea
                  id="outcome"
                  placeholder="What improved, what would you repeat, and what would you change next time?"
                  value={formData.outcome}
                  onChange={(event) => {
                    setFormData((current) => ({ ...current, outcome: event.target.value }))
                    setErrors((current) => ({ ...current, outcome: undefined }))
                  }}
                  rows={3}
                  maxLength={COMMUNITY_STORY_LIMITS.outcome.max}
                  aria-invalid={errors.outcome ? 'true' : 'false'}
                  aria-describedby="community-outcome-help"
                  className="mt-1.5 bg-white"
                />
                <p
                  id="community-outcome-help"
                  role={errors.outcome ? 'alert' : undefined}
                  className={`mt-1 text-xs ${errors.outcome ? 'text-red-600' : 'text-green-950/55'}`}
                >
                  {errors.outcome || `${formData.outcome.length}/${COMMUNITY_STORY_LIMITS.outcome.max} characters (at least ${COMMUNITY_STORY_LIMITS.outcome.min} if filled in)`}
                </p>
              </div>
            </div>
            </fieldset>
          </div>

          <div className="rounded-2xl border border-green-950/10 bg-green-50/45">
            <button
              type="button"
              onClick={() => setShowExtras((open) => !open)}
              aria-expanded={showExtras}
              aria-controls="submit-extras-section"
              className="flex w-full items-center justify-between gap-3 p-4 text-left md:p-5"
            >
              <span>
                <span className="block text-base font-semibold text-green-950">
                  More details
                  <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 align-middle">Optional</span>
                </span>
                <span className="mt-1 block text-sm leading-5 text-green-950/70">
                  Grass type, notes, email for status updates, or social credit.
                </span>
              </span>
              <ChevronDown
                aria-hidden="true"
                className={`h-5 w-5 shrink-0 text-green-950/60 transition-transform ${showExtras ? 'rotate-180' : ''}`}
              />
            </button>

            <div id="submit-extras-section" hidden={!showExtras} className="space-y-4 px-4 pb-4 md:px-5 md:pb-5">
          <div>
            <Label htmlFor="description">Anything else? (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Add a detail that does not fit the story above."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              maxLength={500}
              aria-invalid={errors.description ? 'true' : 'false'}
              aria-describedby="community-photo-description-help"
              className="bg-white"
            />
            {errors.description ? (
              <p id="community-photo-description-help" role="alert" className="text-xs text-red-600 mt-1">{errors.description}</p>
            ) : (
              <p id="community-photo-description-help" className="text-xs text-gray-400 mt-1">
                {formData.description.length}/500
              </p>
            )}
          </div>

          {/* Grass Type */}
          <div>
            <Label htmlFor="grassType">Grass Type (Optional)</Label>
            <Select
              value={formData.grassType}
              onValueChange={(value) => setFormData(prev => ({ ...prev, grassType: value }))}
            >
              <SelectTrigger id="grassType" className="bg-white">
                <SelectValue placeholder="Select your grass type" />
              </SelectTrigger>
              <SelectContent>
                {GRASS_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email">Email (Optional)</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby="community-photo-email-help"
              className="bg-white"
            />
            {errors.email ? (
              <p id="community-photo-email-help" role="alert" className="text-xs text-red-600 mt-1">{errors.email}</p>
            ) : (
              <p id="community-photo-email-help" className="text-xs text-gray-500 mt-1">
                Only used for this submission&apos;s status. This does not subscribe you to email.
              </p>
            )}
          </div>

          {/* Social Media Handle */}
          <div>
            <Label htmlFor="socialHandle">Social Handle (Optional)</Label>
            <Input
              id="socialHandle"
              type="text"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="@yourusername"
              value={formData.socialHandle}
              onChange={(e) => setFormData(prev => ({ ...prev, socialHandle: e.target.value }))}
              maxLength={100}
              className="bg-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Displayed publicly as credit on your photo.
            </p>
          </div>
            </div>
          </div>

          <div className="rounded-2xl border border-green-200 bg-green-50/70 p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="publishing-rights"
                checked={publishingRightsConfirmed}
                onCheckedChange={(checked) => {
                  setPublishingRightsConfirmed(checked === true)
                  if (checked === true) setPublishingRightsError(false)
                }}
                aria-invalid={publishingRightsError}
                aria-describedby="publishing-rights-help"
                className="mt-0.5 h-5 w-5"
              />
              <Label htmlFor="publishing-rights" className="cursor-pointer text-sm font-normal leading-6 text-green-950">
                I took this photo or have permission to share it, and I agree that it may appear publicly in the gallery and be shared by visitors.
              </Label>
            </div>
            <p
              id="publishing-rights-help"
              role={publishingRightsError ? 'alert' : undefined}
              className={`ml-8 mt-2 text-xs ${publishingRightsError ? 'text-red-600' : 'text-green-900/65'}`}
            >
              {publishingRightsError ? 'Confirm you have permission before submitting.' : (
                <>
                  See our <Link href="/terms" className="font-semibold underline underline-offset-2">Terms</Link> and{' '}
                  <Link href="/privacy" className="font-semibold underline underline-offset-2">Privacy Policy</Link>.
                </>
              )}
            </p>
          </div>

          {/* CAPTCHA */}
          <div className="flex justify-center">
            {TURNSTILE_SITE_KEY ? (
              <Turnstile
                key={turnstileResetKey}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onError={() => {
                  setTurnstileToken(null)
                  toast.error('CAPTCHA verification failed. Please try again.')
                }}
                onExpire={() => setTurnstileToken(null)}
                options={{
                  theme: 'light',
                  size: 'normal'
                }}
              />
            ) : (
              <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                Photo submissions are temporarily unavailable. Please try again later.
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <Button
              type="submit"
              size="lg"
              disabled={!selectedFile || !publishingRightsConfirmed || !zipIsResolved || isCompressing || isSubmitting || !TURNSTILE_SITE_KEY || (!turnstileToken && process.env.NODE_ENV === 'production')}
              className="w-full min-h-[48px] text-base"
            >
              {uploadStage === 'uploading' ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : uploadStage === 'submitting' ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Almost done...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  Submit for Review
                </>
              )}
            </Button>
            <p className="text-xs text-gray-400 text-center mt-2">
              Photos are reviewed before publishing. Exact ZIP codes are never stored.
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}
